import { createSelector } from 'reselect';

import {
  getRememberCreditCard,
  getEnableDynamicPaymentGatewayConfig,
  getGatewayType,
} from './config';
import getGiftCard from './getGiftCard';
import getMember from './getMember';
import { PAYMENT_METHOD } from '../constants/paymentMethod';
import { getSelectedPaymentMethods } from '.';
import getCvcToken from './getCvcToken';
import { PAYMENT_GATEWAY_TYPES } from '../constants';

// Helper function to create a generic payment object
const createPayment = (
  type: string,
  amount: number | undefined,
  extraInfo = {},
) => ({
  PaymentType: type,
  Amount: amount,
  ...extraInfo,
});

export default createSelector(
  [
    getSelectedPaymentMethods,
    getRememberCreditCard,
    getGiftCard,
    getMember,
    getEnableDynamicPaymentGatewayConfig,
    getCvcToken,
    getGatewayType,
  ],
  (
    subPayments,
    rememberCreditCard,
    giftCard,
    member,
    enableDynamicPaymentGatewayConfig,
    cvcToken,
    gatewayType,
  ) => {
    if (!subPayments) return undefined;

    return subPayments
      .map(subPayment => {
        const {
          method,
          amount,
          token,
          lastFour,
          receiptText,
          receiptLink,
          cardType,
          referenceNumber,
          authorisationCode,
          Receipt,
        } = subPayment;

        switch (method) {
          case PAYMENT_METHOD.FREE:
            return createPayment('cc', amount);

          case PAYMENT_METHOD.PAY_LATER:
            return createPayment('pt', amount);

          case PAYMENT_METHOD.MEMBER_MONEY:
            return createPayment('hc', amount);

          case PAYMENT_METHOD.MEMBER_REWARDS:
          case PAYMENT_METHOD.MEMBER_POINTS:
            return createPayment('lc', amount);

          case PAYMENT_METHOD.EFTPOS:
            return createPayment('cc', amount, {
              PrintData: receiptText,
              ReceiptLink: receiptLink,
              PaymentInfo: {
                CardType: cardType,
                ReferenceNumber: referenceNumber,
                AuthorisationCode: authorisationCode,
                Receipt,
              },
            });

          case PAYMENT_METHOD.SAVED_CARD:
            return createPayment('cc', amount, {
              PaymentInfo:
                gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE
                  ? {
                      saved_pg_token: token,
                      useIntents: enableDynamicPaymentGatewayConfig,
                      cardCvcToken: cvcToken,
                    }
                  : {
                      pay_method_id: token,
                    },
            });

          case PAYMENT_METHOD.CREDIT_CARD:
            return createPayment('cc', amount, {
              PaymentInfo:
                gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE
                  ? {
                      id: token,
                      remember_pg_token: member ? rememberCreditCard : false,
                      last_four: lastFour,
                      useIntents: enableDynamicPaymentGatewayConfig,
                      paymentMethodType: method,
                    }
                  : {
                      save_pay_method: member && rememberCreditCard,
                    },
            });

          case PAYMENT_METHOD.GOOGLE_PAY:
          case PAYMENT_METHOD.APPLE_PAY:
          case PAYMENT_METHOD.ALIPAY:
            return createPayment('cc', amount, {
              PaymentInfo:
                gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE
                  ? {
                      id: token,
                      useIntents: enableDynamicPaymentGatewayConfig,
                      paymentMethodType: method,
                    }
                  : {},
            });

          case PAYMENT_METHOD.GIFT_CARD:
            return createPayment('gc', amount, {
              GCMemberNo: giftCard.number,
              GCVerifyPIN: giftCard.pin,
            });

          default:
            return undefined;
        }
      })
      .filter(Boolean);
  },
);
